'use client';

import Link from 'next/link';
import { Shield, Brain, CheckCircle, Lock } from 'lucide-react';
import Image from 'next/image';
import { PublicSection } from '../../../../components/public/PublicSection';
import { ProofPanel } from '../../../../components/public/ProofPanel';
import { PROJECT_NAME } from '@shared/constants';
import { LandingPageCard } from '../../landing/components';

/**
 * @group Public
 * @summary Trust and clinical governance section with proof panel
 */
export function TrustGovernanceSection() {
  const trustPoints = [
    {
      icon: Shield,
      title: 'HPCSA-Verified Clinicians',
      description: `Every doctor practising on ${PROJECT_NAME} is fully registered with the Health Professions Council of South Africa (HPCSA) and verified during our rigorous onboarding process. We check qualifications, registration status, and professional standing before any clinician joins our platform. You can view your doctor's HPCSA registration number and qualifications before your consultation. We maintain ongoing compliance monitoring to ensure all clinicians meet professional standards throughout their time with ${PROJECT_NAME}.`,
    },
    {
      icon: Brain,
      title: 'AI Supports, Doctors Decide',
      description: 'Our artificial intelligence technology plays a specific, limited role: it helps triage symptoms, estimates certain vitals, and provides preliminary information to support doctors. However, AI never makes clinical decisions or diagnoses. Every diagnosis, treatment plan, prescription, and medical decision is made by a qualified doctor using their professional judgement and training. AI is a decision-support tool only, designed to make consultations more efficient without compromising clinical autonomy.',
    },
    {
      icon: CheckCircle,
      title: 'Full Regulatory Alignment',
      description: `${PROJECT_NAME} operates in strict accordance with South African telemedicine guidelines and regulations. We comply with HPCSA guidelines on telemedicine practice, including appropriate patient assessment, documentation standards, and limitations of remote care. Our platform includes built-in safety protocols that flag conditions requiring in-person examination. Doctors are trained to recognise "red flag" symptoms and provide clear guidance when emergency care or face-to-face consultation is necessary.`,
    },
    {
      icon: Lock,
      title: 'Privacy, Security, and Data Protection',
      description: 'Your health information is protected under the Protection of Personal Information Act (POPIA). All patient data is encrypted, stored securely within South Africa, and accessible only to you and your treating clinicians. We maintain comprehensive audit trails for accountability and quality assurance. Your data is never sold or shared with third parties for marketing purposes. You control your health records and can download or delete your information at any time.',
    },
  ];

  return (
    <PublicSection 
      heading="Trust, Clinical Governance, and Your Safety"
      subheading={`Your health and safety are paramount. ${PROJECT_NAME} operates within South Africa's strict regulatory framework for telemedicine, and we've built robust clinical governance processes that protect both patients and doctors. Transparency and accountability guide everything we do.`}
      headingColor="hsl(var(--brand-mint))"
      subheadingColor="hsl(var(--brand-mint))"
      separatorColor="hsl(var(--brand-mint))"
      separatorLineColor="hsl(var(--brand-mint) / 0.4)"
      className="bg-brand-teal"
      indented
    >

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        {trustPoints.map((point, idx) => (
          <LandingPageCard
            key={idx}
            icon={point.icon}
            title={point.title}
            description={point.description}
            iconBg="bg-secondary"
            className="p-8"
          />
        ))}
      </div>

      <div className="text-center space-y-4">
        <p className="text-section-inverted-foreground/90 mb-6">
          {PROJECT_NAME} is guided by a diverse board of directors with expertise in healthcare, 
          governance, finance, and technology. Our leadership team brings decades of 
          combined experience in building sustainable, ethical healthcare solutions.
        </p>
        <Link
          href="/board"
          className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-section-inverted-foreground text-section-inverted-foreground rounded-lg font-semibold hover:bg-white/10 transition-colors"
        >
          Meet Our Board of Directors →
        </Link>
      </div>
    </PublicSection>
  );
}
